/**
 * Enquête n°4 — Adapté de l'Étude de Cas CCS n°23
 * (Formation initiale moto : sensibiliser aux effets physiologiques de l'alcool
 *  et leurs incidences sur la conduite)
 *
 * Sources : ONISR 2024, Code de la route R234-1 L234-1, Sécurité routière,
 * INSERM (effets dose-réponse), Étude DRIVE (Pomerleau et al.).
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const alcoolMotoEnquete = {
  slug: 'alcool-moto',
  themeCode: 'L',
  issueNumber: 4,
  totalIssues: 8,
  series: 'enquetes',
  category: 'Enquête physiologique',
  shortTitle: 'Alcool & moto',
  title: '0,2 g/L : pourquoi à moto c\'est différent',
  kicker: 'Enquête n°4 · Série « Témoignages & Sécurité »',
  subtitle:
    "Le seuil légal du jeune permis est à 0,2 g/L — soit moins d'un verre standard. Pourquoi ? Parce que sur 2 roues, perdre l'équilibre se fait avant même de perdre le contrôle. L'alcool fait 30 % des morts moto. Voici exactement ce qui se passe dans ton corps.",
  heroImage:
    'https://images.unsplash.com/photo-1704036626661-e6b5bf636496?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt: 'Bouteille d’alcool posée à côté du volant d’une voiture — illustration alcool et conduite',
  readTimeMin: 13,
  seoDescription:
    'Enquête alcool et conduite moto 2026 : effets dose-réponse, seuil probatoire 0,2 g/L, sanctions, spécificité de l’équilibre sur 2 roues. Conforme au programme officiel thème L.',
  seoKeywords: 'alcool moto 0.2 g/L, permis probatoire alcool, alcoolémie conduite, sanctions alcool deux-roues',

  edito: {
    title: "Le verre de trop n'est pas le 3ᵉ, c'est le 1ʳ",
    body: "On t'a appris à ne pas « boire trop ». Mais sur 2 roues, le problème commence dès le <strong>premier verre</strong>. Pourquoi ? Parce que conduire une moto demande quelque chose qu'une voiture ne demande pas : <strong>l'équilibre</strong>. Or, l'équilibre est géré par le cervelet — zone du cerveau la plus précocement affectée par l'éthanol. <br/><br/>Résultat : <strong>30 % des motards tués</strong> en France en 2024 étaient au-dessus du seuil légal d'alcoolémie (ONISR). Dans 60 % de ces cas, ils étaient en dessous de 1 g/L — c'est-à-dire « juste un peu ». Cette enquête t'explique pourquoi le seuil légal des jeunes permis est à 0,2 g/L — et pourquoi, même s'il était à 0,5 g/L pour toi, tu devrais le respecter comme s'il était à 0,2.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les seuils légaux 2026 : ce que dit vraiment la loi',
      paragraphs: [
        "<strong>Permis probatoire</strong> (3 ans après obtention, 2 ans en AAC) : <strong>0,2 g/L de sang</strong> ou 0,10 mg/L d'air expiré (article R234-1 du Code de la route). En pratique : <strong>zéro</strong>. Un demi de bière standard (25 cl à 5°) = 0,3 g/L pour 70 kg. Donc le moindre verre = infraction.",
        "<strong>Permis classique adulte</strong> : <strong>0,5 g/L</strong> ou 0,25 mg/L. Soit environ 2 verres standards selon morphologie. Au-delà = contravention.",
        "<strong>Au-delà de 0,8 g/L</strong> : c'est un <strong>délit</strong> (art. L234-1). Plus une contravention. La différence est énorme — inscription au casier judiciaire B2, peine de prison possible.",
        "<strong>Sanctions 2026</strong> : contravention (0,5-0,8 g/L) = 135 € + 6 points + suspension administrative jusqu'à 3 ans. Délit (>0,8 g/L) = jusqu'à 4 500 € + 2 ans prison + annulation permis + obligation éthylotest anti-démarrage 5 ans + casier B2.",
        "<strong>Spécificité motard</strong> : depuis 2020, en cas d'accident corporel avec alcool >0,5 g/L, la <strong>responsabilité civile peut être exclue</strong> par l'assureur. Tu paies de ta poche les dégâts causés à autrui. Cas réels : factures de 80 000 à 500 000 €.",
      ],
      keyPoints: [
        'Probatoire : 0,2 g/L = zéro verre en pratique',
        'Adulte : 0,5 g/L = 2 verres max selon morpho',
        '> 0,8 g/L = délit (casier B2, prison possible)',
        'Alcool + accident = exclusion d’assurance',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "L'élimination de l'alcool dans le sang est d'environ <strong>0,1 g/L par heure</strong>, et ce taux ne peut <strong>pas être accéléré</strong> (ni café, ni douche froide, ni sport — ce sont des mythes). Concrètement : si tu bois 4 verres standards à minuit, tu ne redescends en dessous de 0,5 g/L qu'à 6h du matin. Si tu étais en probatoire (seuil 0,2 g/L), <strong>il faut attendre 9h</strong>. C'est pour ça que le piège du « je vais rouler demain matin » est si fréquent : on a oublié ce qu'on avait bu la veille, et l'alcoolémie est encore active.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: "Pourquoi sur 2 roues c'est différent : le cervelet",
      paragraphs: [
        "Sur une voiture, l'alcool dégrade ta vigilance et tes réflexes. Sur une moto, il fait pire : <strong>il dégrade ton équilibre</strong>. Et tu n'as pas 4 roues pour compenser.",
        "<strong>Le cervelet</strong>, partie du cerveau gérant l'équilibre et la coordination fine, est <strong>la zone la plus rapidement affectée</strong> par l'éthanol. À 0,2 g/L : déjà <strong>-15 % de précision motrice</strong>. À 0,5 g/L : -30 %. À 0,8 g/L : -50 %.",
        "<strong>Effet concret à moto</strong> : trajectoires moins précises, freinage moins coordonné (les 2 roues s'utilisent de manière asynchrone, c'est une coordination fine), tendance à <strong>compenser tardivement</strong> les variations d'équilibre (vent latéral, irrégularités du sol). Risque de chute au démarrage, au freinage d'urgence, dans un virage serré.",
        "<strong>Vision tunnel</strong> : autre effet décisif. L'alcool réduit le champ visuel périphérique de 10 à 30 %. Sur une moto, où anticiper les angles morts et les voitures arrivant latéralement est vital, c'est mortel.",
        "<strong>Désinhibition</strong> : enfin, l'alcool désactive partiellement le cortex préfrontal. Tu prends plus de risques. Tu accélères plus. Tu te crois meilleur que tu ne l'es — effet doublé par le sentiment de liberté du motard.",
      ],
      keyPoints: [
        'Cervelet = 1er touché par l’éthanol',
        '0,2 g/L = -15 % précision motrice',
        'Vision tunnel = angles morts non vus',
        'Désinhibition + liberté moto = cocktail mortel',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Je n'ai bu qu'un verre, je peux y aller. » <strong>Un seul verre te place déjà entre 0,2 et 0,3 g/L</strong> selon ta morphologie. Si tu es en probatoire (ce qui est probable jusqu'à 3 ans après ton permis), c'est <strong>retrait de 6 points sur 6</strong> = invalidation immédiate du permis. Six ans d'efforts perdus pour un demi. Si tu es adulte expert : tu es légalement OK, mais physiologiquement déjà à -15 % de coordination. Sur 2 roues, c'est suffisant pour rater un freinage d'urgence ou un virage serré. La seule règle qui marche, c'est : <strong>quand on roule, on ne boit pas — même un peu</strong>.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: '5 stratégies pour ne JAMAIS être dans cette situation',
      paragraphs: [
        "<strong>1. La règle du « éthylotest auto-imposé »</strong>. Garde un éthylotest à usage unique dans ton top-case (1,50 € en grande surface). Avant chaque démarrage après un repas avec alcool : tu testes. Si positif = tu ne démarres pas. Simple.",
        "<strong>2. La règle du SAM (Sans Aucun Modérateur)</strong>. Quand tu sors entre motards, l'un de vous est SAM : 0 alcool, point. Tournez chaque mois. Vous êtes 4 ? Donc 3 fois sur 4 tu peux boire normalement, 1 fois sur 4 tu es responsable.",
        "<strong>3. La règle de l'attente</strong>. Si tu as bu, attends. 1 verre = +1h d'attente avant moto. 2 verres = +2h. 3 verres = +3h. Même si tu te sens bien.",
        "<strong>4. La règle de l'alternative</strong>. Si tu sais que tu vas boire ce soir : prévois transport. Métro, Uber, ami non buveur, dodo sur place. Coût : 15-30 €. Coût d'une suspension permis : 5 000-15 000 €.",
        "<strong>5. La règle des amis</strong>. Tu vois un copain motard avoir bu et vouloir prendre sa moto ? <strong>Tu prends ses clés</strong>. Fâchez-vous 1 soir plutôt que de l'enterrer la semaine suivante. C'est ton vrai rôle d'ami.",
      ],
      keyPoints: [
        '1. Éthylotest dans le top-case (1,50 €)',
        '2. SAM tournant entre motards',
        '3. 1 verre = +1h d’attente min',
        '4. Prévois transport alternatif',
        '5. Prends les clés de tes amis qui ont bu',
      ],
    },
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Quand je sensibilise des candidats permis sur l'alcool (étude de cas n°23), je leur fais d'abord remplir un tableau : leur poids, leur sexe, leur fréquence de sortie, le nombre de verres moyens, le seuil légal applicable. Quasi tous découvrent qu'ils sont régulièrement <em>au-dessus du seuil</em> en pensant respecter la loi. Une jeune femme de 55 kg passe à 0,5 g/L au bout d'<strong>un seul verre de vin à tâble</strong>. Ce n'est pas du moralisme, c'est de la physique. Le « je tiens bien l'alcool » est une <strong>sensation subjective</strong> qui ne déplace pas la molécule d'éthanol dans le sang. Le seul truc qui marche : ne pas boire si tu roules. Point. »",
    },
    {
      type: 'story',
      icon: Users,
      title: "Le témoignage : « Un verre, et 4 ans de Centre pénitentiaire »",
      body: "« J'avais 24 ans, permis A2 obtenu 8 mois avant. Restaurant d'anniversaire en banlieue parisienne avec des potes. J'ai bu un verre de vin blanc à l'apéro, un verre de rouge au repas. <strong>Soit 0,4 g/L. Légal pour un adulte, illegal pour moi en probatoire (seuil 0,2).</strong> <br/><br/>Sur le retour, j'ai voulu dépasser un véhicule sur une départementale. Je me suis « senti bien » — désinhibition de l'alcool. J'ai sous-estimé la distance avec une voiture qui arrivait en face. Choc frontal, l'autre conducteur tué sur le coup. Moi : 4 jours de coma, 8 mois d'hôpital, jambe amputée. <br/><br/>Tribunal : <strong>homicide involontaire aggravé par alcool</strong>. <strong>4 ans de prison ferme.</strong> Pendant ma détention, j'ai reçu la facture d'assurance : 480 000 € pour les ayants droit, exclusion de couverture pour alcoolémie. Je rembourse à vie, sur mon salaire d'invalidité. <strong>Pour 2 verres.</strong> Aujourd'hui je parle dans des collèges et des auto-écoles. » <em>— Antoine, 31 ans, ex-détenu</em>",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '30 %',
    middle: 'des motards tués',
    label: "étaient au-dessus du seuil légal d'alcoolémie",
    source: 'ONISR — Bilan accidentologie moto 2024',
    accent: '#7c3aed',
  },

  glossary: [
    { term: 'Alcoolémie', def: "Concentration d'éthanol dans le sang, exprimée en g/L. Mesurée par prise de sang ou estimée par éthylotest (air expiré)." },
    { term: 'Seuil probatoire (0,2 g/L)', def: "Seuil légal pour les permis de moins de 3 ans (2 ans en AAC). Soit environ 0 verre standard." },
    { term: 'Seuil adulte (0,5 g/L)', def: 'Seuil légal en France pour les permis classiques. Équivaut à environ 2 verres selon morphologie.' },
    { term: 'Cervelet', def: 'Zone du cerveau gérant l’équilibre et la coordination motrice fine. La plus rapidement affectée par l’éthanol.' },
    { term: 'Vision tunnel', def: "Réduction du champ visuel périphérique sous effet de l'alcool. Peut atteindre -30 % au-delà de 0,5 g/L." },
    { term: 'Désinhibition', def: 'Perte de l’autocensure normalement assurée par le cortex préfrontal. Augmente la prise de risque.' },
    { term: 'EAD', def: "Éthylotest Anti-Démarrage. Dispositif obligatoire 5 ans post-condamnation pour délit alcool. Coût mensuel : ~70 €." },
    { term: 'Exclusion d’assurance', def: "Refus de couverture par l'assureur en cas d'alcool>0,5 g/L. Le conducteur paie personnellement les dégâts causés." },
    { term: 'SAM', def: 'Sans Aucun Modérateur : conducteur désigné dans un groupe, qui ne boit absolument rien.' },
    { term: 'Casier judiciaire B2', def: "Volet du casier accessible à l'employeur. Inscription des délits dont conduite >0,8 g/L. Effacé après 10 ans." },
  ],

  ecsrCorner: {
    title: 'Côté formateur ECSR',
    intro:
      "Cet article s'appuie sur l'étude de cas n°23 utilisée en formation des moniteurs ECSR : sensibiliser des candidats permis A1/A2 aux effets physiologiques de l'alcool sur la conduite 2-roues.",
    points: [
      'Public-cible : 6 candidats permis A1/A2 en formation initiale.',
      'Type d’intervention : sensibiliser (aspect physiologique dominant).',
      'Durée : 2h salle + 1h pratique (atelier vision tunnel + Romberg).',
      "Méthode : calcul personnalisé d'alcoolémie + démonstrations + jeu de rôle 'je rentre comment ?'.",
      'Engagement final : 5 règles personnelles écrites, signées par chaque candidat.',
    ],
    seeCasLink: {
      label: "Voir l'étude de cas CCS n°23 complète (jury ECSR)",
      to: '/enseignant/etudes-de-cas',
      testid: 'see-cas-ccs-23',
    },
  },

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur l'alcool, les seuils légaux et les effets sur la conduite 2-roues. Réussite minimum : 8/10.",
    themeCode: 'L',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: {
    slug: 'permis-125-fausse-facilite',
    number: 5,
    title: 'Du B à la 125 : la fausse facilité',
    short: 'Passerelle 125',
    series: 'enquetes',
  },
};

export default alcoolMotoEnquete;
